// ===========================================
// Parent Invoices Page
// ===========================================
// View and pay invoices for the family

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  Alert,
  Skeleton,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import { formatUTCDate } from '../../utils/dateUtils';
import { displayLineDescription } from '../../utils/invoiceLineDescription';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import PageHeader from '../../components/common/PageHeader';
import InvoiceDetailDialog from '../../components/invoices/InvoiceDetailDialog';
import BankTransferDialog from '../../components/invoices/BankTransferDialog';
import {
  useParentInvoices,
  useParentFamilyCredit,
  useCreatePaymentSession,
  useDownloadInvoicePdf,
  useBankTransferDetails,
  useVerifyInvoicePayment,
  useCancelInvoiceCheckout,
} from '../../hooks/useInvoices';
import InvoiceAmendedNote from '../../components/invoices/InvoiceAmendedNote';
import FamilyLateFeeLines from '../../components/invoices/FamilyLateFeeLines';
import { Invoice } from '../../api/invoices.api';
import { formatCurrencyFromDollars, paymentDetailText } from '../../utils/currency';
import CreditOnAccountNotice from '../../components/invoices/CreditOnAccountNotice';
import { appliedCoverageSuffix } from '../../utils/discountCoverage';
import {
  INVOICE_STATUS_COLORS as STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
  invoiceStatusLabel,
  invoiceBalance,
  formatPaymentDay,
} from '../../utils/invoiceDisplay';

// ===========================================
// HELPER FUNCTIONS
// ===========================================

// Invoice money is stored in dollars.
const formatCurrency = formatCurrencyFromDollars;

// ===========================================
// INVOICE CARD COMPONENT
// ===========================================

interface InvoiceCardProps {
  invoice: Invoice;
  onPay: (invoice: Invoice) => void;
  onBankTransfer: (invoice: Invoice) => void;
  onViewDetails: (invoice: Invoice) => void;
  onDownloadPdf: (invoice: Invoice) => void;
  isPaymentLoading: boolean;
  isDownloadLoading: boolean;
  hasBankDetails: boolean;
}

function InvoiceCard({ invoice, onPay, onBankTransfer, onViewDetails, onDownloadPdf, isPaymentLoading, isDownloadLoading, hasBankDetails }: InvoiceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const timeZone = useSchoolTimezone();
  const balance = invoiceBalance(invoice);
  const remainingBalance = balance.amount;
  const isPayable =
    invoice.status === 'SENT' ||
    invoice.status === 'PARTIALLY_PAID' ||
    invoice.status === 'OVERDUE';
  const isOverdue =
    invoice.status !== 'PAID' &&
    invoice.status !== 'CANCELLED' &&
    new Date(invoice.dueDate) < new Date();

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* On a phone the controls take their own row UNDER the title, so the
            number, description and "Amended" note get the full width (Andrew,
            2026-09-28: they were squeezed into a narrow column beside four
            controls). From `sm` up they sit side by side as before. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {invoice.invoiceNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.description || invoice.Term?.name || 'Invoice'}
            </Typography>
            <InvoiceAmendedNote latestAmendment={invoice.latestAmendment} timeZone={timeZone} />
          </Box>
          <Box
            data-testid="invoice-card-actions"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails(invoice)}
            >
              Details
            </Button>
            <IconButton
              size="small"
              onClick={() => onDownloadPdf(invoice)}
              disabled={isDownloadLoading}
              title="Download PDF"
            >
              <DownloadIcon />
            </IconButton>
            <Chip
              label={invoiceStatusLabel(invoice.status)}
              sx={{
                backgroundColor: STATUS_COLORS[invoice.status].bg,
                color: STATUS_COLORS[invoice.status].text,
                fontWeight: 600,
              }}
            />
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ ml: 0.5 }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* #372: each late fee on this invoice (a waived one struck through). */}
        {(invoice.InvoiceLateFee ?? []).length > 0 && (
          <Box sx={{ mb: 1 }}>
            <FamilyLateFeeLines fees={invoice.InvoiceLateFee} timeZone={timeZone} />
          </Box>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Total
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(invoice.total)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Paid
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'success.main', fontWeight: 500 }}
            >
              {formatCurrency(invoice.amountPaid)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Balance
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: balance.owing
                  ? 'warning.main'
                  : invoice.status === 'CANCELLED'
                    ? 'text.secondary'
                    : 'success.main',
              }}
            >
              {formatCurrency(remainingBalance)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Due Date
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: isOverdue ? 'error.main' : 'text.primary' }}
            >
              {formatUTCDate(invoice.dueDate, { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Typography>
          </Grid>
        </Grid>

        {/* Collapsible Content */}
        <Collapse in={expanded}>
          {/* Line Items */}
          {invoice.InvoiceItem && invoice.InvoiceItem.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Items:
              </Typography>
              {invoice.InvoiceItem.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {displayLineDescription(item.description)}
                  </Typography>
                  <Typography variant="body2">{formatCurrency(item.total)}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Discounts */}
          {invoice.InvoiceDiscount && invoice.InvoiceDiscount.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Discounts Applied:
              </Typography>
              {invoice.InvoiceDiscount.map((discount) => (
                <Box
                  key={discount.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {discount.name}
                    {/* #625 D9: what it covers, as the PDF says it. */}
                    {appliedCoverageSuffix(discount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                    -{formatCurrency(discount.calculatedAmount)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Payment History */}
          {invoice.Payment && invoice.Payment.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Payment History:
              </Typography>
              {invoice.Payment.map((payment) => (
                <Box
                  key={payment.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    bgcolor: '#FCF6E6',
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {formatCurrency(payment.amount)}
                    </Typography>
                    {paymentDetailText(payment.processingFee, Number(payment.toCredit ?? 0)) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {paymentDetailText(payment.processingFee, Number(payment.toCredit ?? 0))}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {PAYMENT_METHOD_LABELS[payment.method]}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatPaymentDay(payment.paidAt, timeZone)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Collapse>

        {/* Pay Buttons */}
        {isPayable && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ flex: 1 }}
              startIcon={<PaymentIcon />}
              onClick={() => onPay(invoice)}
              disabled={isPaymentLoading}
            >
              {isPaymentLoading ? 'Processing...' : `Pay ${formatCurrency(remainingBalance)}`}
            </Button>
            {hasBankDetails && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ flex: 1 }}
                startIcon={<AccountBalanceIcon />}
                onClick={() => onBankTransfer(invoice)}
              >
                Pay by Bank Transfer
              </Button>
            )}
          </Box>
        )}

        {invoice.status === 'PAID' && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mt: 1 }}
          >
            This invoice has been paid in full
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// SUMMARY CARD COMPONENT
// ===========================================

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  color?: string;
  icon?: React.ReactNode;
}

function SummaryCard({ title, value, subtitle, color = '#4580E4', icon }: SummaryCardProps) {
  return (
    <Card sx={{ height: '100%', width: '100%' }}>
      <CardContent sx={{ height: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
          {icon && (
            <Box
              sx={{
                bgcolor: `${color}20`,
                borderRadius: 2,
                p: 1.5,
                color: color,
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function ParentInvoicesPage() {
  const schoolSlug = extractSchoolSlugFromPath(window.location.pathname);

  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [bankTransferInvoice, setBankTransferInvoice] = useState<Invoice | null>(null);
  // The Stripe return flag, read once at first render. Held in state because
  // history.replaceState never re-renders: reading the URL on each render left
  // the banner impossible to close.
  const [returnFlag, setReturnFlag] = useState<'success' | 'cancelled' | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') return 'success';
    if (params.get('cancelled') === 'true') return 'cancelled';
    return null;
  });
  const dismissReturnFlag = () => {
    setReturnFlag(null);
    window.history.replaceState({}, '', window.location.pathname);
  };

  // Queries
  const { data: invoices, isLoading, error } = useParentInvoices();
  // #642 Phase 2 Request 6: the family's account credit, shown above the invoices.
  const { data: familyCredit } = useParentFamilyCredit();
  const { data: bankDetails } = useBankTransferDetails();
  const paymentMutation = useCreatePaymentSession();
  const downloadMutation = useDownloadInvoicePdf();
  const verifyPaymentMutation = useVerifyInvoicePayment();
  const cancelCheckoutMutation = useCancelInvoiceCheckout();

  // Back from Stripe WITHOUT paying (cancel_url carries the invoice id): retire
  // that checkout now so the school is not locked out of amending the invoice
  // until it expires. Best-effort; then drop the id from the URL so a refresh
  // does not repeat it (the "Payment was cancelled" notice still shows).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cancelledInvoiceId = params.get('invoice');
    if (params.get('cancelled') === 'true' && cancelledInvoiceId) {
      cancelCheckoutMutation.mutate(cancelledInvoiceId);
      params.delete('invoice');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on return from Stripe

  // Back from Stripe after paying (success_url carries session_id): ask the server
  // to record the payment in case the webhook is late. ONCE per return: the id is
  // read once and removed from the URL BEFORE the call, so a re-render, a refresh
  // or StrictMode's double effect cannot repeat it. (#273: this effect used to
  // depend on the mutation object, which is new on every mutation state change,
  // so it re-ran while session_id was still in the URL — ~5,600 calls from one
  // phone, each a Stripe request, exhausting the school's Stripe rate limit.)
  const verifyStartedRef = useRef(false);
  useEffect(() => {
    if (verifyStartedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    verifyStartedRef.current = true;
    params.delete('session_id');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    verifyPaymentMutation.mutate(sessionId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on return from Stripe

  // Handle view details
  const handleViewDetails = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
  };

  // Handle bank transfer
  const handleBankTransfer = (invoice: Invoice) => {
    setBankTransferInvoice(invoice);
  };

  // Handle PDF download
  const handleDownloadPdf = (invoice: Invoice) => {
    setDownloadingInvoiceId(invoice.id);
    downloadMutation.mutate(
      {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
      },
      {
        onSettled: () => {
          setDownloadingInvoiceId(null);
        },
      }
    );
  };

  // Handle payment
  const handlePay = async (invoice: Invoice) => {
    setPayingInvoiceId(invoice.id);
    try {
      const baseUrl = schoolSlug ? `${window.location.origin}/${schoolSlug}/parent/invoices` : `${window.location.origin}/parent/invoices`;
      const result = await paymentMutation.mutateAsync({
        id: invoice.id,
        successUrl: `${baseUrl}?success=true`,
        // The invoice id lets the page release the checkout lock on return.
        cancelUrl: `${baseUrl}?cancelled=true&invoice=${encodeURIComponent(invoice.id)}`,
      });

      // Redirect to Stripe checkout
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('Payment error:', err);
    } finally {
      setPayingInvoiceId(null);
    }
  };

  // Calculate summary stats
  const totalOutstanding =
    invoices
      ?.filter(
        (i) =>
          i.status === 'SENT' ||
          i.status === 'PARTIALLY_PAID' ||
          i.status === 'OVERDUE'
      )
      .reduce(
        (sum, i) => sum + parseFloat(i.total) - parseFloat(i.amountPaid),
        0
      ) || 0;

  const overdueCount =
    invoices?.filter((i) => i.status === 'OVERDUE').length || 0;

  const paidThisYear =
    invoices
      ?.filter(
        (i) =>
          i.status === 'PAID' &&
          new Date(i.paidAt || i.updatedAt).getFullYear() ===
            new Date().getFullYear()
      )
      .reduce((sum, i) => sum + parseFloat(i.amountPaid), 0) || 0;

  if (isLoading) {
    return (
      <Box>
        <PageHeader title="My Invoices" subtitle="View and pay your invoices" />
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          {[1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={200}
              sx={{ mb: 2 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader title="My Invoices" subtitle="View and pay your invoices" />
        <Alert severity="error">
          Failed to load invoices. Please try again later.
        </Alert>
      </Box>
    );
  }

  // Separate invoices by status
  const unpaidInvoices =
    invoices?.filter(
      (i) =>
        i.status === 'SENT' ||
        i.status === 'PARTIALLY_PAID' ||
        i.status === 'OVERDUE'
    ) || [];
  const paidInvoices = invoices?.filter((i) => i.status === 'PAID') || [];

  return (
    <Box>
      <PageHeader title="My Invoices" subtitle="View and pay your invoices" />

      {/* Payment Result Alerts */}
      {returnFlag === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={dismissReturnFlag}>
          Payment successful! Thank you for your payment. You will receive a receipt via email.
        </Alert>
      )}
      {returnFlag === 'cancelled' && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={dismissReturnFlag}>
          Payment was cancelled. You can try again at any time.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
        <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
          <SummaryCard
            title="Outstanding Balance"
            value={formatCurrency(totalOutstanding)}
            subtitle={`${unpaidInvoices.length} unpaid invoice(s)`}
            color={totalOutstanding > 0 ? '#FFCE00' : '#96DAC9'}
            icon={<ReceiptIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
          <SummaryCard
            title="Overdue"
            value={overdueCount.toString()}
            subtitle="invoice(s) past due date"
            color={overdueCount > 0 ? '#ff4040' : '#96DAC9'}
            icon={<WarningIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
          <SummaryCard
            title="Paid This Year"
            value={formatCurrency(paidThisYear)}
            color="#96DAC9"
            icon={<CheckCircleIcon />}
          />
        </Grid>
      </Grid>

      {/* #642 Phase 2: the family's account credit (nothing when there is none). */}
      {familyCredit && Number(familyCredit.balance) > 0 && (
        <Box sx={{ mb: 4 }}>
          <CreditOnAccountNotice credit={familyCredit} />
        </Box>
      )}

      {/* Unpaid Invoices */}
      {unpaidInvoices.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Invoices Requiring Payment
          </Typography>
          {unpaidInvoices
            .sort((a, b) => {
              // Sort overdue first, then by due date
              if (a.status === 'OVERDUE' && b.status !== 'OVERDUE') return -1;
              if (a.status !== 'OVERDUE' && b.status === 'OVERDUE') return 1;
              return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            })
            .map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onPay={handlePay}
                onBankTransfer={handleBankTransfer}
                onViewDetails={handleViewDetails}
                onDownloadPdf={handleDownloadPdf}
                isPaymentLoading={payingInvoiceId === invoice.id}
                isDownloadLoading={downloadingInvoiceId === invoice.id}
                hasBankDetails={!!bankDetails}
              />
            ))}
        </Box>
      )}

      {/* No Unpaid Invoices */}
      {unpaidInvoices.length === 0 && (
        <Alert severity="success" sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            All caught up!
          </Typography>
          <Typography variant="body2">
            You have no outstanding invoices at this time.
          </Typography>
        </Alert>
      )}

      {/* Paid Invoices */}
      {paidInvoices.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            Payment History
          </Typography>
          {paidInvoices
            .sort(
              (a, b) =>
                new Date(b.paidAt || b.updatedAt).getTime() -
                new Date(a.paidAt || a.updatedAt).getTime()
            )
            .slice(0, 5)
            .map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onPay={handlePay}
                onBankTransfer={handleBankTransfer}
                onViewDetails={handleViewDetails}
                onDownloadPdf={handleDownloadPdf}
                isPaymentLoading={false}
                isDownloadLoading={downloadingInvoiceId === invoice.id}
                hasBankDetails={!!bankDetails}
              />
            ))}
          {paidInvoices.length > 5 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              Showing 5 of {paidInvoices.length} paid invoices
            </Typography>
          )}
        </Box>
      )}

      {/* No Invoices at All */}
      {(!invoices || invoices.length === 0) && (
        <Alert severity="info">
          No invoices found. Invoices will appear here when your school generates them.
        </Alert>
      )}

      {/* Invoice Detail Dialog */}
      <InvoiceDetailDialog
        open={selectedInvoice !== null}
        onClose={() => setSelectedInvoice(null)}
        invoice={selectedInvoice}
        onDownload={handleDownloadPdf}
        isDownloadLoading={selectedInvoice ? downloadingInvoiceId === selectedInvoice.id : false}
        familyCredit={familyCredit}
      />

      {/* Bank Transfer Dialog */}
      <BankTransferDialog
        open={bankTransferInvoice !== null}
        onClose={() => setBankTransferInvoice(null)}
        bankDetails={bankDetails || null}
        invoiceNumber={bankTransferInvoice?.invoiceNumber || ''}
        amountDue={
          bankTransferInvoice
            ? parseFloat(bankTransferInvoice.total) - parseFloat(bankTransferInvoice.amountPaid)
            : 0
        }
      />
    </Box>
  );
}
